import { useMemo } from 'react'

import type { IDataBuilderConfig, IGetSeriesDataBody } from '~/types/graph-builder'

import { useAnalyticsContext } from '~/providers/Analytics'

export const useFormattedBody = ({
  appKey,
  config,
  deviceType,
  kpiField,
  withDefaultInstallationCountry,
}: {
  appKey: string
  config: IDataBuilderConfig
  deviceType: string
  kpiField: string
  withDefaultInstallationCountry?: boolean
}): IGetSeriesDataBody[] => {
  const { state } = useAnalyticsContext()

  const body = useMemo(() => {
    const maxEnd = new Date()
    const nbDaysToIgnore: number = +config.nbLastDaysToIgnore

    let end = new Date(state.computedFiltersDate.range.end)

    maxEnd.setDate(maxEnd.getDate() - (nbDaysToIgnore + 1))

    if (!isNaN(end.getTime()) || maxEnd.getTime() < end.getTime()) {
      end = maxEnd
    }

    const batchedPayload = config.series.map((el) => {
      const singlePayload = {
        app_key: appKey,
        breakdowns: [],
        device_type: deviceType,
        feature_name: config.featureName,
        filters: [
          {
            condition_type: 'gt',
            key: 'installation_date',
            values: [state.computedFiltersDate.range.start],
          },
          {
            condition_type: 'lt',
            key: 'installation_date',
            values: [end.toISOString().split('T')[0]],
          },
        ],
        kpi_values: {
          field: kpiField,
          normalize_by: el.normalizedBy,
          values: el.values,
        },
      }

      state.computedFilters.forEach((filter) => {
        switch (filter.key) {
          case 'breakdown':
          case 'kpi':
          case 'placement_key':
          case 'navigation_graph_id':
          case 'abtest_key':
            return
          case 'installation_country':
            filter.value = withDefaultInstallationCountry ? filter.value.filter((el) => el !== 'WW') : []
            return
        }

        if (filter.value?.length == 0) {
          return
        }

        singlePayload.filters.push({
          condition_type: 'in',
          key: filter.key,
          values: filter.value,
        })
      })

      return singlePayload
    })

    return batchedPayload
  }, [appKey, config, deviceType, kpiField, state, withDefaultInstallationCountry])

  return body
}
